import React, { useEffect, useState, useRef } from "react";
import {
  TextField, Tabs, Tab, Box, Button, CircularProgress, IconButton,
  Paper, List, ListItem, ListItemText, Typography, makeStyles, Grid, Chip, Divider
} from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCar, faBus } from "@fortawesome/free-solid-svg-icons";
import useDebounce from "../helpers/useDebounce";
import { API } from "aws-amplify";
import { getAPIName } from "../../config";
import { useSnackbar } from 'notistack';
import handleApiErrors from "../../common/handleApiErrors";
import {
  GoogleMap,
  Polyline,
  Marker,
  InfoWindow,
  useJsApiLoader,
} from "@react-google-maps/api";

// ── Styles ───────────────────────────────────────────────────────────────────
const useStyles = makeStyles({
  root: { maxWidth: 980, margin: "28px auto", padding: 20, borderRadius: 12, boxShadow: "0 6px 20px rgba(15,15,15,0.06)" },
  row: { display: "flex", gap: 12, alignItems: "center", marginBottom: 12 },
  fieldWrapper: { position: "relative", flex: 1 },
  suggestions: { position: "absolute", top: 64, width: "100%", zIndex: 20, maxHeight: 260, overflow: "auto" },
  vehicleGroup: { display: "flex", gap: 6, alignItems: "center" },
  tabPanel: { background: "#fff", borderRadius: 8 },
  routeHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 },
  totals: { display: "flex", flexDirection: "column", gap: 8 },
  tollItem: { display: "flex", justifyContent: "space-between", width: "100%" },
  smallMuted: { color: "#666", fontSize: 13, textAlign: "left" as const },
  tabs: {
    minHeight: 40,
    "& .MuiTabs-flexContainer": { justifyContent: "flex-start" },
  },
  tab: {
    textTransform: "none" as const,
    minHeight: 40,
    padding: "6px 14px",
    borderRadius: 20,
    marginRight: 10,
    background: "#f3f4f6",
  },
  tabSelected: { background: "#e8ecff", fontWeight: 600, textAlign: "left" as const },
  tollRow: { padding: "14px 0", borderBottom: "1px solid #eee" },
  tabsContainer: { display: "flex", justifyContent: "flex-start", width: "100%" },
  tabIndicator: { display: "none" },
  // Map
  mapWrapper: {
    marginTop: 24,
  },
  mapTitle: {
    fontWeight: 600,
    marginBottom: 8,
    textAlign: "left" as const,
  },
  mapEl: {
    width: "100%",
    height: 440,
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #e0e3ef",
    boxShadow: "0 2px 12px rgba(15,15,15,0.06)",
  },
  mapLegend: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 20,
    alignItems: "center",
    padding: "10px 4px",
    marginTop: 8,
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    color: "#444",
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    flexShrink: 0,
    border: "2px solid #fff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
  },
  legendLine: {
    width: 28,
    height: 4,
    borderRadius: 2,
    flexShrink: 0,
  },
});

// ── Types ─────────────────────────────────────────────────────────────────────
type PlacePrediction  = { description: string; place_id: string };
type Coordinates      = { lat: number; lng: number };
type SelectedPlace    = { description: string; place_id: string };

type Toll = {
  name: string;
  exit: string;
  latitude: number;
  longitude: number;
  cash_value: number;
  etc_value: number;
};

type CongestionZone = {
  name: string;
  estimated_entry_time: string;
  currency?: string;
  min: number;
  max: number;
  latitude: number;
  longitude: number;
};

type RouteObj = {
  route_id: number;
  tolls: Toll[];
  managed_lanes: {
    lanes: any[];
    summary: any[];
  };
  total_cash: number;
  total_etc: number;
  count: number;
  distance_miles: number;
  congestion: { summary: any[]; zones: CongestionZone[] };
  has_congestion: boolean;
  route: [number, number][];
};

type RouteCoordinates = {
  origin_latitude: number | null;
  origin_longitude: number | null;
  destination_latitude: number | null;
  destination_longitude: number | null;
};

const ROUTE_COLORS     = ["#3b6cf7", "#e67e22", "#27ae60", "#8e44ad", "#16a085"];
const ROUTE_COLORS_DIM = ["#6f8df9",  "#e58a3a", "#3da86b", "#a76bcf", "#3fb7a5"];

function makePinSvg(fillColor: string, borderColor: string): string {
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="32" viewBox="0 0 24 32">
      <path 
        d="M12 2C7 2 3 6 3 11c0 7 9 17 9 17s9-10 9-17c0-5-4-9-9-9z"
        fill="${fillColor}"
        stroke="${borderColor}"
        stroke-width="1.5"
      />
      <circle cx="12" cy="11" r="4" fill="#fff"/>
    </svg>
  `;
}

function svgToDataUrl(svg: string): string {
  return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);
}

// ── RouteMap ──────────────────────────────────────────────────────────────────
interface RouteMapProps {
  routes: RouteObj[];
  selectedTab: number;
  directions: any;
  setDirections: (directions: any) => void;
  stripHtml: (s: string) => string;
  apiName: string;
}

const RouteMap: React.FC<RouteMapProps> = ({ routes, selectedTab, directions, setDirections, stripHtml, apiName }) => {
  const classes = useStyles();
  const [activeMarker, setActiveMarker] = useState<any>(null);
  const mapRef = useRef<any>(null);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY!,
  });

  useEffect(() => {
    const fetchDirections = async () => {
      const pts = routes[selectedTab]?.route;
      if (!pts?.length) {
        setDirections(null);
        return;
      }
      try {
        const [originLat, originLng] = pts[0];
        const [destLat, destLng] = pts[pts.length - 1];
        const response = await API.get(apiName, `/api/v1/places/directions`, {
          queryStringParameters: {
            origin_lat: originLat,
            origin_lng: originLng,
            dest_lat: destLat,
            dest_lng: destLng,
          }
        });
        if (response) {
          setDirections({ routes: [response] });
        } else {
          setDirections(null);
        }
      } catch (error) {
        console.error("Error fetching directions:", error);
        setDirections(null);
      }
    };

    fetchDirections();
  }, [routes, selectedTab, apiName, setDirections]);
  if (!isLoaded) return <>Loading map...</>;

  return (
    <div className={classes.mapWrapper}>
      <Typography variant="subtitle1" className={classes.mapTitle}>
        Route Map
      </Typography>

      <GoogleMap
        key={routes.length + "-" + selectedTab}
        onLoad={(map) => {
          mapRef.current = map;
          const pts = routes[selectedTab]?.route;
          if (!pts?.length) return;
          const bounds = new google.maps.LatLngBounds();
          pts.forEach(([lat, lng]) => bounds.extend({ lat, lng }));
          map.fitBounds(bounds, { top: 120, bottom: 120, left: 120, right: 120 });
        }}
        mapContainerStyle={{ width: "100%", height: "440px", borderRadius: "12px" }}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        }}
          
      >
        
        {routes.map((route, routeIndex) => {
          const isSelected = routeIndex === selectedTab;
          const strokeColor =
            (isSelected ? ROUTE_COLORS : ROUTE_COLORS_DIM)[
              routeIndex % ROUTE_COLORS.length
            ];

          return (
            <React.Fragment key={route.route_id}>
              {/* Route polyline */}
              {route.route?.length && (
                <Polyline
                  path={route.route.map(([lat, lng]) => ({ lat, lng }))}
                  options={{
                    strokeColor,
                    strokeOpacity: isSelected ? 1 : 0.7,
                    strokeWeight: isSelected ? 6 : 3,
                    zIndex: isSelected ? 10 : 5,
                  }}
                />
              )}
              {/* Toll & congestion markers */}
              {isSelected && (
                <>
                  {route.tolls.map((toll, i) =>
                    toll.latitude && toll.longitude ? (
                      <Marker
                        key={`toll-${i}`}
                        position={{ lat: toll.latitude, lng: toll.longitude }}
                        icon={{
                          url: svgToDataUrl(makePinSvg("#e74c3c", "#c0392b")),
                          scaledSize: new google.maps.Size(22, 30),
                          anchor: new google.maps.Point(11, 30),
                        }}
                        onClick={() =>
                          setActiveMarker({
                            type: "toll",
                            data: toll,
                            position: { lat: toll.latitude, lng: toll.longitude },
                          })
                        }
                      />
                    ) : null
                  )}

                  {route.managed_lanes?.lanes?.map((lane, laneIndex) => {
                    return (
                      <React.Fragment key={`managed-lane-${laneIndex}`}>
                        {lane.polyline?.length > 0 && (
                          <Polyline
                            path={lane.polyline.map(([lat, lng]: [number, number]) => ({ lat, lng }))}
                            options={{
                              strokeOpacity: 0.6,
                              icons: [
                                {
                                  icon: {
                                    path: "M 0,-1 0,1",
                                    strokeOpacity: 0.6,
                                    scale: 4,
                                    strokeColor: "#00E5FF",
                                  },
                                  offset: "0",
                                  repeat: "10px",
                                },
                              ],
                              zIndex: 16,
                            }}
                          />
                        )}

                        {lane.start && (
                          <Marker
                            position={{ lat: lane.start[0], lng: lane.start[1] }}
                            icon={{
                              url: svgToDataUrl(makePinSvg("#27ae60", "#229954")),
                              scaledSize: new google.maps.Size(22, 30),
                              anchor: new google.maps.Point(11, 30),
                            }}
                            onClick={() =>
                              setActiveMarker({
                                type: "managed-lane-start",
                                data: lane,
                                position: { lat: lane.start[0], lng: lane.start[1] },
                              })
                            }
                          />
                        )}

                        {lane.end && (
                          <Marker
                            position={{ lat: lane.end[0], lng: lane.end[1] }}
                            icon={{
                              url: svgToDataUrl(makePinSvg("#8e44ad", "#6c3483")),
                              scaledSize: new google.maps.Size(22, 30),
                              anchor: new google.maps.Point(11, 30),
                            }}
                            onClick={() =>
                              setActiveMarker({
                                type: "managed-lane-end",
                                data: lane,
                                position: { lat: lane.end[0], lng: lane.end[1] },
                              })
                            }
                          />
                        )}
                      </React.Fragment>
                    );
                  })}

                  {route.has_congestion &&
                    route.congestion?.zones?.map((zone, i) =>
                      zone.latitude && zone.longitude ? (
                        <Marker
                          key={`zone-${i}`}
                          position={{ lat: zone.latitude, lng: zone.longitude }}
                          icon={{
                            url: svgToDataUrl(makePinSvg("#f1c40f", "#d4a017")),
                            scaledSize:new google.maps.Size(22, 30),
                            anchor: new google.maps.Point(11, 30),
                          }}
                          onClick={() =>
                            setActiveMarker({
                              type: "zone",
                              data: zone,
                              position: { lat: zone.latitude, lng: zone.longitude },
                            })
                          }
                        />
                      ) : null
                    )}
                </>
              )}
            </React.Fragment>
          );
        })}

        {activeMarker && (
          <InfoWindow
            position={activeMarker.position}
            onCloseClick={() => setActiveMarker(null)}
            options={{ disableAutoPan: true }}
          >
            <div style={{ fontFamily: "sans-serif", minWidth: 180 }}>
              {activeMarker.type === "toll" ? (
                <>
                  <div style={{ fontWeight: 600 }}>{activeMarker.data.name}</div>
                  <div style={{ color: "#666", fontSize: 13 }}>{activeMarker.data.exit}</div>
                  <div>Cash: ${activeMarker.data.cash_value.toFixed(2)}</div>
                  <div>ETC: ${activeMarker.data.etc_value.toFixed(2)}</div>
                </>
              ) : activeMarker.type === "managed-lane-start" ? (
                <>
                  <div style={{ fontWeight: 600 }}>{activeMarker.data.name}</div>
                  <div style={{ color: "#666", fontSize: 13 }}>Start Point</div>
                  <div>Available: {activeMarker.data.available ? "Yes" : "No"}</div>
                  <div>Current Price: ${activeMarker.data.current_price.toFixed(2)}</div>
                </>
              ) : activeMarker.type === "managed-lane-end" ? (
                <>
                  <div style={{ fontWeight: 600 }}>{activeMarker.data.name}</div>
                  <div style={{ color: "#666", fontSize: 13 }}>End Point</div>
                  <div>Available: {activeMarker.data.available ? "Yes" : "No"}</div>
                  <div>Current Price: ${activeMarker.data.current_price.toFixed(2)}</div>
                </>
              ) : (
                <>
                  <div style={{ fontWeight: 600 }}>{activeMarker.data.name}</div>
                  <div style={{ color: "#666", fontSize: 13 }}>
                    Entry: {activeMarker.data.estimated_entry_time}
                  </div>
                  <div>
                    {activeMarker.data.max
                      ? `${activeMarker.data.currency || "$"}${activeMarker.data.min} - ${activeMarker.data.currency || "$"}${activeMarker.data.max}`
                      : "Free"}
                  </div>
                </>
              )}
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      <div className={classes.mapLegend}>
        {routes.map((_, i) => {
          const isSelected = i === selectedTab;
          return (
            <div className={classes.legendItem} key={i}>
              <div
                className={classes.legendLine}
                style={{
                  background: isSelected
                    ? ROUTE_COLORS[i % ROUTE_COLORS.length]
                    : ROUTE_COLORS_DIM[i % ROUTE_COLORS_DIM.length],
                  opacity: isSelected ? 1 : 0.6,
                }}
              />
              <span>Route {i + 1}{isSelected ? " (selected)" : ""}</span>
            </div>
          );
        })}
        <div className={classes.legendItem}>
          <div className={classes.legendDot} style={{ background: "#e74c3c" }} />
          <span>Toll</span>
        </div>
        <div className={classes.legendItem}>
          <div className={classes.legendLine} style={{ background: "#00E5FF" }} />
          <span>Managed Lane</span>
        </div>
        <div className={classes.legendItem}>
          <div className={classes.legendDot} style={{ background: "#27ae60" }} />
          <span>Managed Lane Start</span>
        </div>
        <div className={classes.legendItem}>
          <div className={classes.legendDot} style={{ background: "#8e44ad" }} />
          <span>Managed Lane End</span>
        </div>
        <div className={classes.legendItem}>
          <div className={classes.legendDot} style={{ background: "#f1c40f" }} />
          <span>Congestion Zone</span>
        </div>
      </div>

      {/* ── Google Maps Directions ── */}
      {directions?.routes?.[0]?.legs?.[0]?.steps?.length > 0 && (
        <Box style={{ marginTop: 20, padding: 12, border: "1px solid #e0e3ef", borderRadius: 12, backgroundColor: "#f9f9fb" }}>
          <Typography style={{ fontWeight: 600, marginBottom: 12, textAlign: "left" }}>
            Turn-by-Turn Directions
          </Typography>
          <Box style={{ maxHeight: 320, overflowY: "auto", paddingRight: 8 }}>
            <List dense style={{ padding: 0 }}>
              {directions.routes[0].legs[0].steps.map((step: any, i: number) => (
                <ListItem key={i} style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 6, paddingBottom: 6 }}>
                  <ListItemText
                    primary={`${i + 1}. ${stripHtml(step.html_instructions)}`}
                    secondary={`${step.distance?.text || ""} • ${step.duration?.text || ""}`}
                    primaryTypographyProps={{ style: { fontSize: 13, fontWeight: 500 } }}
                    secondaryTypographyProps={{ style: { fontSize: 12, color: "#999", marginTop: 2 } }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
export default function TollCalculator(): JSX.Element {
  const classes = useStyles();
  const apiName = getAPIName();
  const { enqueueSnackbar } = useSnackbar();

  const [originInput, setOriginInput]             = useState("");
  const [destinationInput, setDestinationInput]   = useState("");
  // Debounced values for autocomplete API calls
  const debouncedOrigin = useDebounce(originInput, 400);
  const debouncedDestination = useDebounce(destinationInput, 400);
  // Autocomplete suggestions
  const [originSuggestions, setOriginSuggestions] = useState<PlacePrediction[]>([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState<PlacePrediction[]>([]);
  // Dropdown visibility
  const [isOriginOpen, setIsOriginOpen] = useState(false);
  const [isDestinationOpen, setIsDestinationOpen] = useState(false);
  // Selected places (description + place_id only — no coordinates needed here)
  const [origin, setOrigin] = useState<SelectedPlace | null>(null);
  const [destination, setDestination] = useState<SelectedPlace | null>(null);
  // Vehicle type — kept as its own state, read directly at submit time
  const [vehicleType, setVehicleType] = useState<"car" | "bus">("car");
  // Resolved coordinates — updated when both places are selected
  const [coordinates, setCoordinates] = useState<RouteCoordinates>({
    origin_latitude: null,
    origin_longitude: null,
    destination_latitude: null,
    destination_longitude: null,
  });

  // Results
  const [departureTime, setDepartureTime] = useState<string>("");
  const [routes, setRoutes] = useState<RouteObj[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [directions, setDirections] = useState<any>(null);
  const stripHtml = (s = "") => s.replace(/<[^>]*>/g, "");

  // Fetch autocomplete suggestions
const fetchPlaceSuggestions = async (query: string): Promise<PlacePrediction[]> => {
  if (query.length < 3) return [];
  try {
    const response = await API.get(apiName, `/api/v1/places/autocomplete`, {
      queryStringParameters: { query }
    });
    return response || [];
  } catch (error) {
    console.error("Error fetching place suggestions:", error);
    return [];
  }
};

const fetchCoordinates = async (
  originPlaceId: string,
  destinationPlaceId: string
): Promise<{ origin: Coordinates; destination: Coordinates } | null> => {
  try {
    const response = await API.get(apiName, `/api/v1/places/coordinates`, {
      queryStringParameters: {
        origin_place_id: originPlaceId,
        destination_place_id: destinationPlaceId
      }
    });
    return response;
  } catch (error) {
    console.error("Error fetching coordinates:", error);
    setIsLoading(false);
    return null;
  }
};

  // Effect for origin suggestions
  useEffect(() => {
    const getSuggestions = async () => {
      if (isOriginOpen && debouncedOrigin) {
        const suggestions = await fetchPlaceSuggestions(debouncedOrigin);
        setOriginSuggestions(suggestions);
      } else {
        setOriginSuggestions([]);
      }
    };
    getSuggestions();
  }, [debouncedOrigin, isOriginOpen]);

  // Effect for destination suggestions
  useEffect(() => {
    const getSuggestions = async () => {
      if (isDestinationOpen && debouncedDestination) {
        const suggestions = await fetchPlaceSuggestions(debouncedDestination);
        setDestinationSuggestions(suggestions);
      } else {
        setDestinationSuggestions([]);
      }
    };
    getSuggestions();
  }, [debouncedDestination, isDestinationOpen]);

  // Fetch and store coordinates once both places are known
  const fetchAndSetCoordinates = async (originPlaceId: string, destinationPlaceId: string) => {
    try {
      const result = await fetchCoordinates(originPlaceId, destinationPlaceId);
      if (result) {
        setCoordinates({
          origin_latitude: result.origin.lat,
          origin_longitude: result.origin.lng,
          destination_latitude: result.destination.lat,
          destination_longitude: result.destination.lng,
        });
      }
    } catch (error) {
      console.error("Error setting coordinates:", error);
    }
  };

  const handleSelectOrigin = async (p: PlacePrediction) => {
    setOriginInput(p.description);
    setOrigin({ description: p.description, place_id: p.place_id });
    setIsOriginOpen(false);
    setOriginSuggestions([]);
    if (destination?.place_id) await fetchAndSetCoordinates(p.place_id, destination.place_id);
  };

  const handleSelectDestination = async (p: PlacePrediction) => {
    setDestinationInput(p.description);
    setDestination({ description: p.description, place_id: p.place_id });
    setIsDestinationOpen(false);
    setDestinationSuggestions([]);
    if (origin?.place_id) await fetchAndSetCoordinates(origin.place_id, p.place_id);
  };
  // Clear origin when user edits the text manually
  const handleOriginInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOriginInput(e.target.value);
    setOrigin(null);
    setCoordinates(prev => ({ ...prev, origin_latitude: null, origin_longitude: null }));
    setIsOriginOpen(true);
  };

  // Clear destination when user edits the text manually
  const handleDestinationInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDestinationInput(e.target.value);
    setDestination(null);
    setCoordinates(prev => ({ ...prev, destination_latitude: null, destination_longitude: null }));
    setIsDestinationOpen(true);
  };

  const isFormValid = (): boolean =>
    !!(coordinates.origin_latitude && coordinates.origin_longitude &&
       coordinates.destination_latitude && coordinates.destination_longitude);

  // ── Submit ───────────────────────────────────────────────────────────────
  const calculateTolls = async () => {
    if (!isFormValid()) {
      enqueueSnackbar("Please fill in all required fields.", { variant: "error" });
      return;
    }
    setIsLoading(true);
    const payload = {
      vehicle_type  : vehicleType,
      origin        : { latitude: coordinates.origin_latitude, longitude: coordinates.origin_longitude },
      destination   : { latitude: coordinates.destination_latitude, longitude: coordinates.destination_longitude },
      departure_time: departureTime || undefined,
    };
    try {
      const response: any = await API.post(apiName, "/api/v1/tolls_calculator", { body: payload });
      setRoutes(response.data || []);
      setSelectedTab(0);
    } catch (error) {
      handleApiErrors(error, enqueueSnackbar);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Grid container>
        <Grid item xs={9}>
          <Typography variant="h4" align="left">Toll Calculator</Typography>
        </Grid>
      </Grid>
      <Divider style={{ marginTop: 10 }} />

      <Paper className={classes.root}>
        {/* ── Input row ── */}
        <div className={classes.row}>
          {/* Origin */}
          <div className={classes.fieldWrapper}>
            <TextField
              label="From" fullWidth variant="outlined" value={originInput}
              onFocus={() => setIsOriginOpen(true)}
              onChange={handleOriginInputChange}
              onBlur={() => setTimeout(() => setIsOriginOpen(false), 150)}
            />
            {isOriginOpen && originSuggestions.length > 0 && (
              <Paper className={classes.suggestions}>
                <List dense>
                  {originSuggestions.map((s, i) => (
                    <ListItem button key={s.place_id || i} onMouseDown={() => handleSelectOrigin(s)}>
                      <ListItemText primary={s.description} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </div>

          {/* Destination */}
          <div className={classes.fieldWrapper}>
            <TextField
              label="To" fullWidth variant="outlined" value={destinationInput}
              onFocus={() => setIsDestinationOpen(true)}
              onChange={handleDestinationInputChange}
              onBlur={() => setTimeout(() => setIsDestinationOpen(false), 150)}
            />
            {isDestinationOpen && destinationSuggestions.length > 0 && (
              <Paper className={classes.suggestions}>
                <List dense>
                  {destinationSuggestions.map((s, i) => (
                    <ListItem button key={s.place_id || i} onMouseDown={() => handleSelectDestination(s)}>
                      <ListItemText primary={s.description} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </div>

          {/* Vehicle type */}
          <div className={classes.vehicleGroup}>
            <IconButton onClick={() => setVehicleType("car")} color={vehicleType === "car" ? "primary" : "default"}>
              <FontAwesomeIcon icon={faCar} />
            </IconButton>
            <IconButton onClick={() => setVehicleType("bus")} color={vehicleType === "bus" ? "primary" : "default"}>
              <FontAwesomeIcon icon={faBus} />
            </IconButton>
          </div>

          <TextField
            type="datetime-local" label="Departure Time"
            InputLabelProps={{ shrink: true }} value={departureTime}
            onChange={e => setDepartureTime(e.target.value)}
          />
          <Button
            variant="contained" color="primary"
            disabled={!isFormValid() || isLoading}
            onClick={calculateTolls}
          >
            {isLoading ? <CircularProgress size={20} /> : "Calculate"}
          </Button>
        </div>

        {/* ── Results ── */}
        {routes.length > 0 && (
          <>
            <Typography variant="subtitle1" style={{ textAlign: "left", marginBottom: 10 }}>
              Route summary:
            </Typography>

            <div className={classes.tabsContainer}>
              <Tabs
                value={selectedTab}
                onChange={(_, v) => setSelectedTab(v)}
                className={classes.tabs}
                classes={{ indicator: classes.tabIndicator }}
                textColor="primary"
              >
                {routes.map((route, i) => (
                  <Tab
                    key={route.route_id}
                    classes={{ root: classes.tab, selected: classes.tabSelected }}
                    label={`Route ${i + 1}`}
                  />
                ))}
              </Tabs>
            </div>

            {routes.map((route, index) => (
              <div hidden={selectedTab !== index} key={route.route_id}>
                <Divider style={{ marginTop: 10 }} />
                {selectedTab === index && (
                  <Box className={classes.tabPanel}>
                    <div className={classes.routeHeader}>
                      <div className={classes.totals}>
                        <Typography style={{ textAlign: "left", marginTop: 10 }}>
                          <strong>Distance:</strong> {route.distance_miles} miles
                        </Typography>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Chip label={`Cash $${route.total_cash.toFixed(2)}`} />
                          <Chip label={`Electronic Toll Collection $${route.total_etc.toFixed(2)}`} />
                          <Typography className={classes.smallMuted}>
                            {route.count} toll{route.count > 1 ? "s" : ""}
                          </Typography>
                        </div>
                      </div>
                    </div>

                    <Divider />

                    <Box style={{ padding: 0 }}>
                      <List style={{ padding: 0 }}>
                        {route.tolls.map((toll, ti) => (
                          <ListItem key={ti} className={classes.tollRow}>
                            <Grid container alignItems="center">
                              <Grid item xs={8}>
                                <div style={{ fontWeight: 600 }}>{toll.name}</div>
                                <div className={classes.smallMuted}>{toll.exit}</div>
                              </Grid>
                              <Grid item xs={4} style={{ textAlign: "right" }}>
                                <div className={classes.tollItem}>
                                  <div className={classes.smallMuted}>Cash</div>
                                  <div><strong>${toll.cash_value.toFixed(2)}</strong></div>
                                </div>
                                <div className={classes.tollItem}>
                                  <div className={classes.smallMuted}>ETC</div>
                                  <div><strong>${toll.etc_value.toFixed(2)}</strong></div>
                                </div>
                              </Grid>
                            </Grid>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Box>
                )}

                {route.managed_lanes?.lanes && route.managed_lanes.lanes.length > 0 && (
                  <Box style={{ marginTop: 16 }}>
                    {/* <Divider style={{ marginBottom: 12 }} /> */}
                    <Typography style={{ fontWeight: 600, textAlign: "left" }}>
                      Managed Lanes :
                    </Typography>
                    <List style={{ padding: 0 }}>
                      {route.managed_lanes.lanes.map((lane: any, laneIndex: number) => {
                        // const summaryCurrency = route.managed_lanes.summary?.[0]?.currency || "$";
                        const summaryMin = lane.min_price ?? 0;
                        const summaryMax = lane.max_price ?? 0;
                        const priceDisplay = !summaryMin  && !summaryMax 
                          ? "$0" 
                          : `$${summaryMin.toFixed(2)} - $${summaryMax.toFixed(2)}`;
                        
                        return (
                          <ListItem key={laneIndex} className={classes.tollRow}>
                            <Grid container alignItems="center">
                              <Grid item xs={8}>
                                <div style={{ fontWeight: 600 }}>{lane.name}</div>
                              </Grid>
                              <Grid item xs={4} style={{ textAlign: "right" }}>
                                  <div><strong>{priceDisplay}</strong></div>
                              </Grid>
                            </Grid>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                )}
              {route.has_congestion && route.congestion?.zones?.length > 0 && (
                <Box style={{ marginTop: 16 }}>
                  <Typography style={{ fontWeight: 600, textAlign: "left" }}>
                    Congestion Charges :
                  </Typography>

                  <List style={{ padding: 0 }}>
                    {route.congestion.zones.map((cz: any, i: number) => {
                      const currency = cz.currency || "$";
                      const isFree = !cz.max || cz.max === 0;

                      return (
                        <ListItem key={i} className={classes.tollRow}>
                          <Grid container alignItems="center">
                            <Grid item xs={8}>
                              <div style={{ fontWeight: 600 }}>{cz.name}</div>
                              <div className={classes.smallMuted}>{cz.estimated_entry_time}</div>
                            </Grid>

                            <Grid item xs={4} style={{ textAlign: "right" }}>
                              <div>
                                <strong>
                                  {isFree
                                    ? "$0"
                                    : `${currency}${cz.min.toFixed(2)} - ${currency}${cz.max.toFixed(2)}`}
                                </strong>
                              </div>
                            </Grid>
                          </Grid>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}
              </div>
            ))}

            {/* ── Google Map ── */}
            <RouteMap routes={routes} selectedTab={selectedTab} directions={directions} setDirections={setDirections} stripHtml={stripHtml} apiName={apiName} />
          </>
        )}
      </Paper>
    </>
  );
}